import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { collection, getDocs, query, where } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    address: "",
    postcode: "",
    gearbox: "",
    area: "",
    instructorId: "",
    acceptTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [areas, setAreas] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error for this field when user starts typing/selecting
    if (errors[name]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const { register } = useAuth();
  const { toast } = useToast();

  // Fetch areas on component mount
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const db = getFirestore();
        const areasSnapshot = await getDocs(collection(db, "areas"));
        const areasData = areasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAreas(areasData);
      } catch (error) {
        console.error("Error fetching areas:", error);
      } finally {
        setLoadingAreas(false);
      }
    };

    fetchAreas();
  }, []);

  // Fetch instructors when area is selected
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!formData.area) {
        setInstructors([]);
        return;
      }

      setLoadingInstructors(true);
      try {
        const db = getFirestore();
        const instructorsQuery = query(
          collection(db, "users"),
          where("role", "==", "instructor"),
          where("area", "==", formData.area)
        );
        const instructorsSnapshot = await getDocs(instructorsQuery);
        const instructorsData = instructorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInstructors(instructorsData);
      } catch (error) {
        console.error("Error fetching instructors:", error);
      } finally {
        setLoadingInstructors(false);
      }
    };

    fetchInstructors();
  }, [formData.area]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setErrors({});

    // Validation
    const validationErrors: any = {};
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError("Please fill in all required fields");
      return;
    }
    
    if (!formData.instructorId) {
      validationErrors.instructorId = "Please select an instructor";
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setError("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!formData.acceptTerms) {
      setError("You must accept the Terms and Privacy Policy");
      return;
    }

    setIsLoading(true);

    try {
      const success = await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        postcode: formData.postcode,
        gearbox: formData.gearbox,
        area: formData.area,
        instructorId: formData.instructorId,
        role: 'student',
      });

      if (success) {
        toast({
          title: "Registration successful",
          description: "Your student account has been created.",
        });
        navigate(`/login?role=student`);
      } else {
        setError("Email already exists or registration failed");
        toast({
          variant: "destructive",
          title: "Registration failed",
          description: "An account with this email already exists or registration failed",
        });
      }
    } catch (err) {
      setError("An error occurred during registration. Please try again.");
      console.error("Registration error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <motion.div 
            className="bg-white rounded-lg shadow-xl overflow-hidden border border-blue-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="px-6 py-8">
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <h1 className="text-3xl font-bold text-road-900 bg-clip-text text-transparent bg-gradient-to-r from-drivewise-600 to-blue-600">Create Your Student Account</h1>
                </motion.div>
                <p className="text-road-600 mt-2">
                  Join Way2Learn Academy and begin your journey to becoming a confident driver
                </p>
              </div>

              {error && (
                <motion.div 
                  className="mb-6 bg-red-50 text-red-800 p-3 rounded-md text-sm border-l-4 border-red-500"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="John"
                      required
                      className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Smith"
                      required
                      className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(01) 234 5678"
                      className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                    required
                    className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postcode">
                    Postcode <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    placeholder="Enter your postcode"
                    required
                    className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gearbox">
                    Preferred Gearbox <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.gearbox}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, gearbox: value }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select gearbox type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="area">
                      Area <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="area"
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      disabled={loadingAreas}
                    >
                      <option value="">{loadingAreas ? 'Loading areas...' : 'Select an area'}</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.name}>
                          {area.name} ({area.postcode})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="instructorId">
                      Select Instructor *
                    </Label>
                    <select
                      id="instructorId"
                      name="instructorId"
                      value={formData.instructorId}
                      onChange={handleInputChange}
                      className={`w-full h-10 px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring ${
                        errors.instructorId ? 'border-red-500' : 'border-input'
                      }`}
                      disabled={loadingInstructors || !formData.area}
                    >
                      <option value="">{loadingInstructors ? 'Loading instructors...' : formData.area ? 'Select an instructor' : 'Please select an area first'}</option>
                      {instructors.map((instructor) => (
                        <option key={instructor.id} value={instructor.id}>
                          {instructor.firstName} {instructor.lastName}
                        </option>
                      ))}
                    </select>
                    {errors.instructorId && (
                      <p className="text-sm text-red-500 mt-1">{errors.instructorId}</p>
                    )}
                    {formData.area && instructors.length === 0 && !loadingInstructors && (
                      <p className="text-sm text-orange-500 mt-1">No instructors available in this area</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        required
                        className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Must be at least 8 characters
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        required
                        className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <Checkbox
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          acceptTerms: checked === true,
                        }));
                      }}
                      className="text-drivewise-500 focus:ring-drivewise-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <Label htmlFor="acceptTerms" className="text-gray-700 font-normal">
                      I accept the{" "}
                      <a href="#" className="text-drivewise-600 hover:text-drivewise-800 hover:underline">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a href="#" className="text-drivewise-600 hover:text-drivewise-800 hover:underline">
                        Privacy Policy
                      </a>
                    </Label>
                  </div>
                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-drivewise-600 to-blue-600 hover:from-drivewise-700 hover:to-blue-700 text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating Account..." : "Create Student Account"}
                  </Button>
                </motion.div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-road-600">
                  Already have an account?{" "}
                  <Link to="/login" className="text-drivewise-600 hover:text-drivewise-800 font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
                <p className="text-road-600 mt-2">
                  <Link to="/forgot-password" className="text-drivewise-600 hover:text-drivewise-800 text-sm hover:underline">
                    Forgot your password?
                  </Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
